import React, { Component } from 'react'
import PropTypes from 'prop-types'

import { Row, Content } from '../components/Layout.js'
import Hero from '../components/Hero'
import Footer from '../components/Footer'
import Button from '../components/Button'
import Card from '../components/Card'
import Diagram from '../components/Diagram'
import Carousel from '../components/Carousel'
import Video from '../components/Video'

import 'gsap'
import 'animation.gsap'
import 'ScrollToPlugin'

/* global TimelineMax, Power3 */

const heroOptions = {
  header: '<strong>bronze</strong><br>framework.<sup>42</sup>',
  buttons: [
    {
      icon: 'ac-mail',
      text: 'Try a demo',
      link: 'http://portal.framework42.com/demo',
      target: '_blank'
    },
    {
      icon: 'ac-login',
      text: 'Login to your system',
      link: 'http://portal.framework42.com',
      target: '_blank'
    }
  ],
  subtext:
    'One system, multiple opportunities to improve life with a smarter, more intelligent IoT enablement solution.',
  background: 'f42'
}

const connect = ['people', 'machines', 'devices', 'things', 'data']

const videoOptions = {
  video: 'https://storage.googleapis.com/bronze-labs-static/media/f42-explain'
}

const points = [
  {
    text: 'Smart Utilities.',
    icon: 'ac-bulb-on'
  },
  {
    text: 'Smart Transport.',
    icon: 'ac-train'
  },
  {
    text: 'Smart Citizens.',
    icon: 'ac-gps-area'
  },
  {
    text: 'Smart Government.',
    icon: 'ac-official-building'
  },
  {
    text: 'Smart Construction.',
    icon: 'ac-forklift'
  },
  {
    text: 'Smart Defence.',
    icon: 'ac-shield'
  },
  {
    text: 'Smart Healthcare.',
    icon: 'ac-heart'
  },
  {
    text: 'Smart ICT.',
    icon: 'ac-signal-tower'
  }
]

class Framework42 extends Component {
  componentDidMount () {
    // animated connect text
    function changeText (container) {
      const word = connect[Math.floor(connect.length * Math.random())]
      container.innerText = word
    }

    const options = {
      start: {
        opacity: 0,
        y: -40,
        ease: Power3.easeOut
      },
      middle: {
        y: 0
      },
      end: {
        opacity: 0,
        y: 40,
        ease: Power3.easeIn
      }
    }
    const containers = document.querySelectorAll('.rotateText')

    containers.forEach((container, index) => {
      const timeline = new TimelineMax({
        repeat: -1,
        delay: index
      })
      timeline
        // animate each of the letters, with an 800ms animation duration, stagger letters with a 0ms delay
        // easing and transitions are set within options objects above
        .from(container, 0.8, options.start)
        // before transitioning out letters, hold for 2 seconds
        .fromTo(container, 0.2, options.middle, options.end, '=+2')
        .call(changeText, [container])
    })
  }
  render () {
    const { featureVideos, clients } = this.props
    return (
      <div className='mainContainer'>
        <Hero options={heroOptions} />
        <section className='noTopPadding'>
          <Row>
            <Content classes={['contentFull']}>
              <h2>
                Explain framework.<sup>42</sup>
              </h2>
            </Content>
          </Row>
          <Row>
            <Content classes={['contentFull']}>
              <span>
                Take control of your city and connect anything to everything. We
                believe to create a successful smart city, you need to connect
                people to people, people to things, and things to things, all
                from one smart Internet of Things (IoT) enablement solution for
                a happier, healthier, scalable city environment.
              </span>
            </Content>
          </Row>
          <Row>
            <Content classes={['contentFull']}>
              <div className='posRel'>
                <Video active options={videoOptions} />
              </div>
            </Content>
          </Row>
          <Row>
            <Content classes={['contentFull', 'center']}>
              <h2>
                We connect
                <div className='rotateTextContainer'>
                  <div className='rotateText'>devices</div>
                </div>
                with
                <div className='rotateTextContainer'>
                  <div className='rotateText'>things</div>
                </div>
              </h2>
              <p className='noMargin middle'>
                Framework.<sup>42</sup> adds a layer between new and legacy
                systems, sensors and devices, enabling 2-way communication. It
                can be used as a stand-alone case management system and is also
                able to easily integrate into any CRM or back office system.
              </p>
              <p className='middle'>
                Framework.<sup>42</sup> also allows you to grow with the city
                and add any new system, sensor or device, making sure that you
                are secured for the future.
              </p>
              <Diagram />
            </Content>
          </Row>
        </section>
        <section id='enable' className='alternate'>
          <div className='backgroundHex' />
          <div className='backgroundHex' />
          <Row>
            <Content classes={['contentFull']}>
              <h2>Enable your industries full potential.</h2>
            </Content>
          </Row>
          <Row>
            <Content classes={['contentFull', 'noTopPadding']}>
              <p>
                Quickly expand the amount of data and information available to
                you in your<br />industry, and enable transformative changes to
                your operations.
              </p>
            </Content>
          </Row>
          <Row>
            <Content classes={['contentFull']}>
              {points.map((point, index) => (
                <div className='point' key={index}>
                  <i className={'ac ' + point.icon} />
                  {point.text}
                </div>
              ))}
            </Content>
          </Row>
        </section>
        <section>
          <Row>
            <Content classes={['contentFull']}>
              <h2>Why would it benefit me?</h2>
            </Content>
          </Row>
          <Row>
            <Content classes={['contentFull']}>
              <span>
                Framework.<sup>42</sup> is a continually evolving product and
                offers scalability with your business. It allows you to use as a
                stand-alone case management system and is also able to easily
                integrate into any CRM or back office system.
              </span>
            </Content>
          </Row>
          {featureVideos && featureVideos.length > 0 && (
            <Carousel itemsToShow={1} autoplay={false}>
              {featureVideos.map((card, index) => (
                <Card video full options={card} key={index} />
              ))}
            </Carousel>
          )}
          <Row>
            <Content classes={['contentFull']}>
              <h2>Where is it being used?</h2>
            </Content>
          </Row>
          {clients && clients.length > 0 && (
            <Carousel>
              {clients.map((card, index) => (
                <Card options={card} key={index} />
              ))}
            </Carousel>
          )}
        </section>
        <section id='interested' className='alternate'>
          <div className='backgroundHex' />
          <div className='backgroundHex' />
          <Row>
            <Content classes={['contentFull']}>
              <h2>Interested?</h2>
            </Content>
          </Row>
          <Row>
            <Content classes={['contentFull', 'noPadding']}>
              <p>
                To find out more about framework.<sup>42</sup>
                <br />and to discuss your needs.
              </p>
            </Content>
          </Row>
          <Row>
            <Content classes={['contentFull']}>
              <Button
                icon='ac-mail'
                classes={['button-primary noMargin']}
                text='Get in touch'
                href='mailto:info@bronzelabs.co.uk?subject=I want to find out more about framework42&body=Hello, I would love to hear more about Framework42, here are my details for you to contact me.'
              />
            </Content>
          </Row>
        </section>
        <Footer />
      </div>
    )
  }
}

Framework42.propTypes = {
  featureVideos: PropTypes.arrayOf(PropTypes.object).isRequired,
  clients: PropTypes.arrayOf(PropTypes.object).isRequired
}

export default Framework42



// WEBPACK FOOTER //
// ./src/containers/Framework42.js